import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'src/lib/forms/Form';
import { getWorkspaceLabelGroups } from 'src/selectors/labelGroups';
import WorkspaceSystem from 'src/systems/WorkspaceSystem';
import LabelGroup from 'src/entities/labelGroup';

type UseFormLabelGroupsOptionsParams = {
	selectedWorkspaceID: number | null,
	isLabelGroupsFeatureAvailable: boolean,
	formState: Form,
	setFormState: Dispatch<SetStateAction<Form>>
};

const DEMO_LABEL_GROUP_NAME = 'verybusy demo';

const NO_LABEL_GROUPS_OPTION = {
	label: 'There are no label groups available. You can create a new one from the workspace settings.',
	value: 'no-label-groups',
	disabled: true
};

const isDemoLabelGroup = ( group: LabelGroup ): boolean => (
	group.name.toLowerCase() === DEMO_LABEL_GROUP_NAME
);

const mapLabelGroupsToOptions = ( labelGroups: LabelGroup[], isLabelGroupsFeatureAvailable: boolean ) => (
	labelGroups.map( ( labelGroup: LabelGroup ) => {
		const isDemo = isDemoLabelGroup( labelGroup );
		return {
			label: labelGroup.name,
			value: labelGroup.id.toString(),
			disabled: !isLabelGroupsFeatureAvailable && !isDemo
		};
	} )
);

const useFormLabelGroupsOptions = ( {
	selectedWorkspaceID,
	isLabelGroupsFeatureAvailable,
	formState,
	setFormState
}: UseFormLabelGroupsOptionsParams ) => {
	const dispatch = useDispatch();
	const workspaceSystem = new WorkspaceSystem( dispatch );
	const labelGroups = useSelector(
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		state => getWorkspaceLabelGroups( state, { workspaceID: selectedWorkspaceID } )
	);

	const options = useMemo( () => {
		const projectLabelGroups = labelGroups?.project_label_groups || [];
		return projectLabelGroups.length > 0
			? mapLabelGroupsToOptions( projectLabelGroups, isLabelGroupsFeatureAvailable )
			: [ NO_LABEL_GROUPS_OPTION ];
	}, [ labelGroups, isLabelGroupsFeatureAvailable ] );

	const onOptionChange = useCallback( ( values: string[] ) => {
		setFormState( prevState => prevState
			.set( 'selectedLabelGroups', values.map( id => parseInt( id, 10 ) ) ) );
	}, [ setFormState ] );

	useEffect( () => {
		if ( !selectedWorkspaceID ) { return; }
		workspaceSystem.getLabelGroups( selectedWorkspaceID );
	}, [ selectedWorkspaceID ] );

	const selected = useMemo( () => [
		...( formState.data.selectedLabelGroups?.map( ( id: number ) => id.toString() ) || [] )
	], [ formState.data.selectedLabelGroups ] );

	return {
		options,
		selected,
		onOptionChange
	};
};

export default useFormLabelGroupsOptions;
